import React, { useEffect, useRef, useState } from "react";
import { useFeedback } from "../../hooks";
import { Button, Dropdown, RadioButton } from "kci-design-lib";
import { useI18n } from "../../i18n/I18nProvider";
import { kciEventBus } from '../../eventBus/KCiEventBus';
import { KCI_APP_IDS } from '../../eventBus/appIds';

interface FeedbackModalProps {
  onClose: () => void;
}

const FEEDBACK_OPTIONS = [
  { id: "general", label: "General" },
  { id: "accuracy", label: "Accuracy of responses" },
  { id: "speed", label: "Speed / performance" },
  { id: "usability", label: "Usability / experience" },
  { id: "feature", label: "Feature request" },
  { id: "bug", label: "Bug / issue" },
];

// Map UI feedback IDs to backend enum values
const FEEDBACK_TYPE_MAP: Record<string, 'General' | 'Bug' | 'Feature request' | 'Performance' | 'Other'> = {
  general: 'General',
  accuracy: 'Performance',
  speed: 'Performance',
  usability: 'Other',
  feature: 'Feature request',
  bug: 'Bug',
  custom: 'Other',
};

const MAX_FEEDBACK_CHAR = 750;

type FeedbackDropdownOption = {
  id: string;
  label: React.ReactNode;
  filterText?: string;
};


export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  onClose,
}) => {
  const { t } = useI18n();

  const handleProductPageClick = () => {
    console.log("Clara AI Product Page clicked");
  };
  const [feedbackType, setFeedbackType] = useState(FEEDBACK_OPTIONS[0].id);
  const [customFeedback, setCustomFeedback] = useState("");
  const [rating, setRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showCategoryError, setShowCategoryError] = useState(false);
  const [showTextError, setShowTextError] = useState(false);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const { isSubmitting, error, submit } = useFeedback();

  // Computed values
  const feedbackOptionsForDropdown: FeedbackDropdownOption[] = FEEDBACK_OPTIONS.map(opt => ({
    id: opt.id,
    label: t(`feedback.option.${opt.id}`),
    filterText: t(`feedback.option.${opt.id}`),
  }));
  const remainingCharacters = `${feedback.length} / ${MAX_FEEDBACK_CHAR}`;

  const isSubmitDisabled = !feedbackType || !rating || !feedback.trim() || isSubmitting;

  useEffect(() => {
    const previouslyFocusedElement = document.activeElement as HTMLElement | null;

    dialogRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement?.focus?.();
    };
  }, [onClose]);

const handleFeedbackTypeSelect = (option: FeedbackDropdownOption) => {
  setFeedbackType(String(option.id));
  setShowCategoryError(false);
};

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const isCategoryInvalid = !feedbackType;
    const isRatingInvalid = rating === null;
    const isTextInvalid = !feedback.trim();

    setShowCategoryError(isCategoryInvalid);
    setShowTextError(isTextInvalid || isRatingInvalid);

    if (isCategoryInvalid || isRatingInvalid || isTextInvalid || isSubmitting) {
      return;
    }

    setSubmitError(null);

    try {
      const payload = {
        feedbackType: FEEDBACK_TYPE_MAP[feedbackType] || 'Other',
        rating: rating!,
        feedback: [customFeedback.trim(), feedback.trim()].filter(Boolean).join("\n"),
      };

      // Submit feedback via backend API
      await submit(payload);
      kciEventBus.publish(
          "kci.shell.toast",
          { message: "Feedback submitted successfully.", type: "success" },
          { source: KCI_APP_IDS.SHELL },
        );
      // Success: reset form and close modal
      setFeedbackType(FEEDBACK_OPTIONS[0].id);
      setCustomFeedback("");
      setRating(null);
      setFeedback("");
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit feedback';
      setSubmitError(errorMessage);
    }
  };

  const handleFeedbackChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value.slice(0, MAX_FEEDBACK_CHAR);
    setFeedback(value);
    if (value.trim()) setShowTextError(false);
  };

  return (
    <div className="feedback-modal-backdrop" role="presentation">
      <div
        className="feedback-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedbackModalTitle"
        tabIndex={-1}
      >
        <div className="feedback-modal__header">
          <h2 id="feedbackModalTitle" className="feedback-modal__title">
            {t('feedback.header')}
          </h2>
          <Button
            buttonType="Tertiary"
            state="Default"
            size="Small"
            classType="Standard"
            theme="Square"
            type="button"
            buttonPrefix
            prefixInstance="Close"
            variant="Outline"
            aria-label={t('common.close')}
            onClick={onClose}
            buttonClass="feedback-modal__close"
          />
        </div>

        <form className="feedback-modal__form" onSubmit={handleSubmit}>
          <div className="feedback-modal__body">
            <p className="feedback-modal__description">
              {t('feedback.description.1')}
              <br />
              {t('feedback.description.2.1')}
              {" "}
              <Button
                buttonType="Tertiary"
                state="Default"
                size="Small"
                classType="Standard"
                theme="Square"
                text
                label={t('feedback.description.2.2')}
                onClick={handleProductPageClick}
                buttonClass="feedback-modal__link feedback-modal__link-button"
                type="button"
              />
              {" "}
              {t('feedback.description.2.3')}
            </p>

            <div className="feedback-modal__field">
              <label
                className="feedback-modal__field-label"
                htmlFor="feedbackType"
              >
                {t('feedback.categoryLabel')}
              </label>
              <div className="feedback-modal__select-container">
                <Dropdown
                  options={feedbackOptionsForDropdown}
                  defaultValue={feedbackType}
                  onSelect={handleFeedbackTypeSelect}
                  placeholder={t('feedback.categoryLabel')}
                  state="default"
                  hasFavorites
                  dropdownAppendTo="self"
                  dropdownClass="feedback-modal__select-trigger"
                  dropdownPanelClass="feedback-modal__select-menu"
                  dropdownProps={{ inputId: "feedbackType" }}
                  customFeedbackPlaceholder={t('feedback.customFeedbackPlaceholder')}
                  onCustomFeedbackChange={(value) => {
                    setCustomFeedback(value);
                    setFeedbackType(value.trim() ? 'custom' : '');
                    setShowCategoryError(false);
                  }}
                />
                {showCategoryError && (
                  <p className="feedback-modal__field-error">{t('feedback.categoryError')}</p>
                )}
              </div>
            </div>

            {/* Rating */}
            <fieldset className="feedback-modal__field feedback-modal__rating-field">
              <legend className="feedback-modal__field-label">
                {t('feedback.ratingLabel')}
              </legend>

              <div className="feedback-modal__rating-options">
                <RadioButton
                  name="overallRating"
                  options={[1, 2, 3, 4, 5].map((value) => ({
                    id: `feedback-rating-${value}`,
                    label: String(value),
                  }))}
                  selectedValue={rating === null ? "" : `feedback-rating-${rating}`}
                  onChange={(value) => {
                    const parsed = Number(value.replace("feedback-rating-", ""));
                    if (!Number.isNaN(parsed)) setRating(parsed);
                  }}
                  isLabel
                  size="Small"
                  radioButtonProps={{ className: "feedback-modal__radio-input" }}
                />
              </div>
            </fieldset>

            {/* Textarea */}
            <label htmlFor="feedbackDetails" className="feedback-modal__sr-only">
              {t('feedback.detailsLabel')}
            </label>
            <div className="feedback-modal__textarea-wrapper">
              <textarea
                id="feedbackDetails"
                className={`feedback-modal__textarea${showTextError ? " feedback-modal__textarea--error" : ""}`}
                value={feedback}
                onChange={handleFeedbackChange}
                aria-describedby="feedbackWordCounter"
              />
              <div className="feedback-modal__textarea-footer">
                <p className="feedback-modal__field-error" style={{ visibility: showTextError ? "visible" : "hidden" }}>
                  {t('feedback.ratingValidationMessage')}
                </p>
                <p id="feedbackWordCounter" className="feedback-modal__word-counter" aria-live="polite">
                  {remainingCharacters}
                </p>
              </div>
            </div>

            {/* Privacy footnote */}
            <p className="feedback-modal__privacy-warning">
              {t('feedback.footnote0')}{" "}
              <span className="feedback-modal__privacy-warning-emphasis">
                {t('feedback.footnote1')}
              </span>{" "}
              {t('feedback.footnote2')}
            </p>
          </div>

          {/* Footer buttons */}
          <div className="feedback-modal__footer">
            {(submitError || error) && (
              <div className="feedback-modal__error" role="alert">
                {submitError || error}
              </div>
            )}
            <button
              className="feedback-modal__button feedback-modal__button--cancel"
              type="button"
              onClick={onClose}
            >
              {t('common.cancel')}
            </button>

            <button
              className="feedback-modal__button feedback-modal__button--submit"
              type="submit"
              disabled={isSubmitDisabled}
            >
              {t('common.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;